<template>
	<el-dialog
		:title="$t('DisputeDialog.dispute_invoice')"
		:visible.sync="visible"
		:before-close="handleClose"
		:width="'520px'">
		<el-form ref="form" :model="form" label-position="top" :rules="rules">
			<el-form-item prop="dispute_code" :label="$t('DisputeDialog.dispute_code')">
				<el-select v-model="form.dispute_code" :placeholder="$t('DisputeDialog.dispute_code')" @change="onDisputeCodeChange">
					<el-option
						v-for="dispute_code in sortedDisputeCodes"
						:key="dispute_code.reference"
						:label="$translate.dispute_code(dispute_code.code)"
						:value="dispute_code.reference"
					/>
				</el-select>
			</el-form-item>
			<el-form-item :label="$t('DisputeDialog.note')">
				<el-input v-model="form.note" type="textarea" :rows="2" :placeholder="$t('DisputeDialog.note')"></el-input>
			</el-form-item>
			<el-form-item :label="$t('DisputeDialog.assignee')">
				<el-select v-model="form.assignee" :placeholder="$t('DisputeDialog.assignee')">
					<el-option :key="'unassigned'" :label="'Niemand'" :value="'unassigned'" />
					<el-option
						v-for="user in participantUsers"
						:key="user.person_by_reference.reference"
						:label="user.person_by_reference.username"
						:value="user.person_by_reference.reference"
					/>
				</el-select>
			</el-form-item>
			<el-form-item :label="$t('DisputeDialog.days_to_suspend')">
				<el-input type="number" min="0" :max="maxDaysToSuspend" :value="form.days_to_suspend" :placeholder="$t('DisputeDialog.days_to_suspend')" @input="handleDaysToSuspendInput" ></el-input>
			</el-form-item>
		</el-form>
		<span slot="footer" class="dialog-footer">
			<el-button @click="handleClose">{{ $t("shared.Cancel") }}</el-button>
			<el-button :loading="loading" type="primary" @click="onSubmit">{{ $t("shared.Confirm") }}</el-button>
		</span>
	</el-dialog>
</template>
<script>
import gql from 'graphql-tag';
import axios from 'axios';
import { mapGetters } from 'vuex';

const GET_CODES = gql`
	query ($participant: uuid!) {
		dispute_codes: risolto_dispute_code (
			distinct_on: [code]
			where: {
				_or: [
					{ participant: { _eq: $participant } },
					{ participant: { _is_null: true } }
				]
			}
			order_by: [
				{ code: asc },
				{ participant: desc_nulls_last }
			]
		) {
			reference
			code
			default_assignee
			default_days_to_suspend
		}
	}`;

export default {
	apollo: {
		dispute_codes: {
			query: GET_CODES,
			variables() {
				return {
					'participant': this.participant
				};
			}
		}
	},
	props: {
		visible: { type: Boolean, required: true},
		participant: { type: String, required: true },
		legalEntity: { type: String, required: true },
		recipient: { type: String, required: true },
		invoiceDispute: { type: Object, required: false, default: null },
		invoices: { type: Array, required: true }
	},
	data() {
		return {
			loading: false,
			dispute_codes: [],
			form: {
				dispute_code: '',
				note: '',
				assignee: '',
				days_to_suspend: 0
			},
			rules: {
				dispute_code: [{ required: true, message: this.$t('fieldRequired') }]
			},
			maxDaysToSuspend: 90
		};
	},
	computed: {
		...mapGetters(['participantUsers', 'loggedInUser']),
		sortedDisputeCodes() {
			return [...this.dispute_codes || []]
				.filter(code => code.default_days_to_suspend !== -1)
				.sort((a, b) => {
					if (this === undefined) {
						return a.code < b.code ? -1 : 1;
					}
					// Sort alphabetically in the locale of the user
					return (this.$translate.dispute_code(a.code) < this.$translate.dispute_code(b.code)) ? -1 : 1;
				});
		},
		mutationVariables() {
			return {
				participant: this.participant,
				legal_entity: this.legalEntity,
				recipient: this.recipient,
				invoices: this.invoices,
				dispute_code: this.form.dispute_code,
				assignee: this.form.assignee,
				days_to_suspend: this.form.days_to_suspend,
				note: this.form.note
			};
		}
	},
	watch: {
		visible(value) {
			if (this.invoiceDispute) {
				this.form.dispute_code = this.invoiceDispute.dispute_code;
				this.form.assignee = this.invoiceDispute.tasks[0].assignee ? this.invoiceDispute.tasks[0].assignee : 'unassigned';
				this.form.note = this.invoiceDispute.tasks[0]?.notes[0]?.note;

				const dispute_code = this.invoiceDispute.dispute_code_by_reference;

				this.form.days_to_suspend = dispute_code.default_days_to_suspend;
				this.maxDaysToSuspend = Math.max(90, dispute_code.default_days_to_suspend);
			} else {
				this.form = {
					dispute_code: '',
					note: '',
					assignee: this.loggedInUser?.reference || '',
					days_to_suspend: 0
				};

				this.maxDaysToSuspend = 90;
			}
		}
	},
	methods: {
		onDisputeCodeChange(value) {
			const dispute_code = this.dispute_codes.find((dispute_code) => { return dispute_code.reference === value; });

			this.form.assignee = dispute_code.default_assignee || this.loggedInUser?.reference || '';
			this.form.days_to_suspend = dispute_code.default_days_to_suspend;
			this.maxDaysToSuspend = Math.max(90, dispute_code.default_days_to_suspend);
		},

		handleDaysToSuspendInput(value) {
			this.form.days_to_suspend = Math.min(Math.max(0, value), this.maxDaysToSuspend);
		},

		handleClose() {
			this.$emit('dialog-close');
		},

		async onSubmit() {
			if (this.loading) {
				return;
			}

			this.$refs['form'].validate(async (valid) => {
				if (valid) {
					await this.submit();
				} else {
					return false;
				}
			});
		},

		async submit() {
			this.loading = true;
			const payload = this.mutationVariables;

			if (this.form.assignee === 'unassigned') {
				payload.assignee = null;
			}

			try {
				await axios.post(`https://${process.env.VUE_APP_API_ENDPOINT}/task/v1/create-invoice-dispute`, payload, {
					headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
				});
				this.$emit('confirm');
				this.handleClose();
			} catch (e) {
				//
			} finally {
				this.loading = false;
			}
		}
	}
};
</script>
<style lang="scss" scoped>
.el-form {
	width: 480px;
}

.el-select {
	width: 100%;
}
</style>
